// This composable finds the fields a custom item is filled into, so adding one doesn't overwrite what the AFP entered by hand
import type { FieldValueResult } from '@/components/documents/Foxit/FormMapping/composables/FieldValueProvider'
import { useFormFillHelper } from '@/components/documents/Foxit/FormMapping/composables/FormFillHelper'
import { useSupplementalFields } from '@/components/documents/Foxit/FormMapping/composables/SupplementalFields'
import type { CustomCartItem } from '@/types'

export const useCustomItemFields = () => {
	const { mapFields, trackItemsRead } = useFormFillHelper()
	const { getSupplementalFields } = useSupplementalFields()

	// The custom item's fields are the ones mapped by the form's field groups that read it,
	// plus the supplemental page when those overflow. Totals, including a field group's own total,
	// are left to the PDF's calculations.
	const getCustomItemFieldNames = (
		fields: string[],
		customItemId: CustomCartItem['id']
	) => {
		return fields.flatMap((field) => {
			const { result: fieldValues, itemsRead } = trackItemsRead(() =>
				mapFields([field])
			)

			if (!itemsRead.some((item) => item.id === customItemId)) {
				return []
			}

			return [
				...fieldValues.filter(({ isTotal }) => !isTotal),
				...getSupplementalFields(fieldValues),
			].map(({ name }) => name)
		})
	}

	const getCustomItemFormMappings = (
		formMappings: FieldValueResult[],
		fields: string[],
		customItemId: CustomCartItem['id']
	) => {
		const fieldNames = getCustomItemFieldNames(fields, customItemId)

		return formMappings.filter(({ name }) => fieldNames.includes(name))
	}

	return {
		getCustomItemFormMappings,
	}
}
